import React, { useState, useEffect, useRef } from 'react';
import Cell from './Cell';
import '../styles/Board.css';

const Board = ({ board, handleColumnClick, handleRowShift, lastMove, shiftedRow, fallingChips, gamePhase, winningPositions }) => {
  const rows = board.length;
  const cols = board[0] ? board[0].length : 0;
  const isDropPhase = gamePhase === 'drop' || gamePhase === 'interim';
  const [rowAnimations, setRowAnimations] = useState({
    row: null,
    direction: null
  });
  
  // Initialize state for the drop animation
  const [chipTransit, setChipTransit] = useState({
    active: false,
    col: null,
    currentRow: -1,
    player: null,
    targetRow: null,
    animationComplete: false // Flag to indicate when animation is finished
  });
  

  
  // Reference for managing animation timeouts
  const timeoutRef = useRef([]);
  

  
  // Handle row shifting animations
  useEffect(() => {
    if (!shiftedRow) return;
    
    setRowAnimations({
      row: shiftedRow.index,
      direction: shiftedRow.direction
    });
    
    // Reset animations after they complete
    const timer = setTimeout(() => {
      setRowAnimations({ row: null, direction: null });
    }, 600); // Match with animation duration
    
    return () => clearTimeout(timer);
  }, [shiftedRow]);
  
  // Animation state - simpler approach with direct prop passing
  const [animatingChip, setAnimatingChip] = useState(null);

  // Handle drop animation showing chip moving through cells
  useEffect(() => {
    // Don't start a new animation if there's no lastMove
    if (!lastMove) return;
    
    // Clean up any existing timeouts
    timeoutRef.current.forEach(id => clearTimeout(id));
    timeoutRef.current = [];
    
    // Get the chip properties
    const targetCol = lastMove.col;
    const targetRow = lastMove.row;
    const player = board[targetRow][targetCol]; // Get player color

    // Set which chip we're currently animating (to hide it during drop)
    setAnimatingChip({ row: targetRow, col: targetCol });
    
    // Initialize animation state to start above the board
    setChipTransit({
      active: true,
      col: targetCol,
      currentRow: -1, // Start above the board
      player,
      targetRow
    });
    
    // Define how long each row transition takes, with acceleration
    const getRowDelay = (row) => {
      const baseDelay = 110;
      const accelerationFactor = 15;
      return Math.max(45, baseDelay - (row * accelerationFactor));
    };
    
    // Pre-calculate all delays to avoid closures in loops
    const rowDelays = [];
    for (let row = 0; row <= targetRow; row++) {
      rowDelays.push(getRowDelay(row));
    }
    
    // Start with a small initial delay
    let cumulativeDelay = 50;
    
    // Schedule each row movement (avoid closure in loop issue)
    for (let i = 0; i <= targetRow; i++) {
      const rowIndex = i; // Capture current row in this scope
      const rowDelay = rowDelays[i] || 50;
      
      // Schedule this row's animation
      const timer = setTimeout(() => {
        setChipTransit(prev => ({
          ...prev,
          currentRow: rowIndex
        }));
      }, cumulativeDelay);
      
      timeoutRef.current.push(timer);
      cumulativeDelay += rowDelay;
    }
    
    // Schedule final cleanup after animation completes
    const finalTimer = setTimeout(() => {
      setChipTransit(prev => ({
        ...prev, 
        active: false
      }));
      
      // Allow the real chip to be shown
      setAnimatingChip(null);
    }, cumulativeDelay + 200); // Additional time for visual smoothness
    
    timeoutRef.current.push(finalTimer);
    
    // Clean up function
    return () => {
      timeoutRef.current.forEach(id => clearTimeout(id));
      timeoutRef.current = [];
      
      // Reset all state
      setChipTransit({
        active: false,
        col: null,
        currentRow: -1,
        player: null,
        targetRow: null
      });
      
      // Ensure no chip is stuck in animating state
      setAnimatingChip(null);
    };
  // board is intentionally excluded: this animation should only restart when a
  // new chip is dropped (lastMove changes), not on every unrelated board update
  // such as a push - otherwise it replays on whatever chip a shift moves into
  // the old drop target cell.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMove]);
  
  // Determine animation class for each row
  const getRowClass = (rowIndex) => {
    let className = "row";
    if (rowAnimations.row === rowIndex) {
      className += rowAnimations.direction === 'left' ? " shifting-left" : " shifting-right";
    }
    return className;
  };

  // Check if a row has any chips in it (for shift eligibility)
  const rowHasChips = (rowIndex) => {
    return board[rowIndex].some(cell => cell !== null);
  };
  
  // Handle row shift buttons
  const handleRowShiftClick = (rowIndex, direction) => {
    if (gamePhase === 'shift' && rowHasChips(rowIndex)) {
      handleRowShift(rowIndex, direction);
    }
  };

  // Helper to render an individual cell
  const renderCell = (row, col) => {
    const value = board[row][col];
    const isNew = lastMove && lastMove.row === row && lastMove.col === col;
    const isFalling = fallingChips.some(chip => chip.to && chip.to.row === row && chip.to.col === col);
    const fallDistance = isFalling ? 
      fallingChips.find(chip => chip.to.row === row && chip.to.col === col).to.row - 
      fallingChips.find(chip => chip.to.row === row && chip.to.col === col).from.row : 0;
    const isWinning = winningPositions && winningPositions.some(pos => pos.row === row && pos.col === col);
    
    // Check if this cell contains a transit chip (only pass transit info to cells that need it)
    const transitInfo = chipTransit.active && chipTransit.col === col ? chipTransit : null;
    
    // Check if this cell's chip is currently being animated
    const isAnimating = animatingChip && animatingChip.row === row && animatingChip.col === col;
    
    return (
      <Cell
        value={value}
        row={row}
        col={col}
        isNew={isNew}
        isFalling={isFalling}
        fallDistance={fallDistance}
        isWinning={isWinning}
        transitInfo={transitInfo}
        isAnimating={isAnimating}
      />
    );
  };

  return (
    <div className="board-container">
      {/* Board always maintains the same layout structure regardless of phase */}
      <div className="board">
        
        {/* Board grid */}
        <div className="board-grid">
          {/* Column markers above the grid - always visible but only active during drop phase */}
          <div className="row-wrapper column-marker-wrapper">
            {/* Empty left spacer to match shift buttons */}
            <div className="shift-button-spacer"></div>
            
            <div className="column-markers">
              {[...Array(cols)].map((_, colIndex) => (
                <div
                  key={`marker-${colIndex}`}
                  className={`column-marker ${isDropPhase && !board[0][colIndex] ? 'active' : isDropPhase ? 'full' : 'inactive'}`}
                  onClick={() => isDropPhase && !board[0][colIndex] && handleColumnClick(colIndex)}
                  aria-label={`Drop chip in column ${colIndex + 1}`}
                >
                  ↓
                </div>
              ))}
            </div>

            {/* Empty right spacer to match shift buttons */}
            <div className="shift-button-spacer"></div>
          </div>

          {/* Create rows with their row shift controls */}
          {[...Array(rows)].map((_, rowIndex) => (
            <div key={rowIndex} className="row-wrapper">
              {/* Left shift button for this row */}
              <button
                className={`shift-button ${gamePhase === 'shift' && rowHasChips(rowIndex) ? 'active' : 'inactive'}`}
                onClick={() => handleRowShiftClick(rowIndex, 'left')}
                disabled={gamePhase !== 'shift' || !rowHasChips(rowIndex)}
                aria-label="Shift row left"
              >
                ←
              </button>

              {/* The actual row */}
              <div className={getRowClass(rowIndex)}>
                {/* Create cells within each row */}
                {[...Array(cols)].map((_, colIndex) => (
                  <div
                    key={rowIndex * cols + colIndex}
                    className="cell-container"
                    onClick={() => isDropPhase && handleColumnClick(colIndex)}
                  >
                    {renderCell(rowIndex, colIndex)}
                  </div>
                ))}
              </div>
              
              {/* Right shift button for this row */}
              <button 
                className={`shift-button ${gamePhase === 'shift' && rowHasChips(rowIndex) ? 'active' : 'inactive'}`}
                onClick={() => handleRowShiftClick(rowIndex, 'right')}
                disabled={gamePhase !== 'shift' || !rowHasChips(rowIndex)}
                aria-label="Shift row right"
              >
                →
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Board;
